import React, { useState } from 'react';
import { Check } from 'lucide-react';

const addons = {
  security: {
    name: 'Security Package',
    description: 'Advanced security features',
    price: 2000
  },
  support: {
    name: 'Extended Support',
    description: 'Additional monthly support',
    price: 1000
  },
  performance: {
    name: 'Performance Optimization',
    description: 'Custom optimization setup',
    price: 2500
  }
};

const plans = {
  starter: {
    name: 'Starter Package',
    price: 2500,
    features: [
      'Pre-built supervised learning models',
      'Swagger setup for up to 15 API endpoints',
      'Basic endpoint and response validation',
      'OAuth2/JWT authentication',
      'Custom API Documentation Interface',
      'CI/CD setup',
      '1 month of email support and basic documentation',
      '2 rounds of revisions'
    ]
  },
  growth: {
    name: 'Growth Package',
    price: 5000,
    features: [
      'One Advanced model selection',
      'Real-Time Predictive Analytics',
      'Advanced Swagger setup for up to 30 API endpoints',
      'OAuth2/JWT authentication',
      'Custom API Documentation Interface',
      'Full test suite, load, and security testing',
      'Multi-environment CI/CD setup with Docker',
      '2 months of priority support and team training',
      '4 rounds of revisions'
    ]
  },
  enterprise: {
    name: 'Enterprise Package',
    price: 12000,
    features: [
      'Comparisive Advanced models selection',
      'Real-Time Predictive Analytics',
      'Enterprise-level Swagger for 50 API endpoints',
      'OAuth2/JWT authentication',
      'Custom API Documentation Interface',
      'Full test automation and testing',
      'Multi-environment CI/CD setup with Docker',
      'Premium support and team training',
      'Custom security and compliance setup',
      '3 months of support, unlimited revisions',
      '5 rounds of revisions'
    ]
  }
};

const PricingPage = ({ onSelectPlan }) => {
  const [selectedAddons, setSelectedAddons] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);

  const toggleAddon = (addonKey) => {
    setSelectedAddons(prev => ({
      ...prev,
      [addonKey]: !prev[addonKey]
    }));
  };

  const calculateTotal = () => {
    if (!selectedPlan) return 0;
    let total = plans[selectedPlan].price;
    Object.entries(selectedAddons).forEach(([key, isSelected]) => {
      if (isSelected) {
        total += addons[key].price;
      }
    });
    return total;
  };

  const getSelectedAddons = () => {
    return Object.entries(selectedAddons)
      .filter(([_, isSelected]) => isSelected)
      .map(([key]) => addons[key]);
  };

  const handleProceedToCheckout = () => {
    const selectedAddonsList = getSelectedAddons();
    onSelectPlan({
      ...plans[selectedPlan],
      addons: selectedAddonsList,
      finalPrice: calculateTotal(),
      billingCycle: 'one-time'
    });
  };

  // Show floating summary when plan is selected
  const shouldShowSummary = selectedPlan !== null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 relative">
      {/* Navigation */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <a href="/" className="text-2xl font-bold text-blue-600">PredictFlow</a>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 hover:text-blue-600">Return to Home</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Transparent Pricing for Every Scale
          </h1>
          <p className="text-xl text-gray-600">
            Choose the perfect plan for your predictive analytics needs
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {Object.entries(plans).map(([key, plan]) => (
            <div
              key={key}
              className={`bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 ${
                selectedPlan === key ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setSelectedPlan(key)}
            >
              <div className="p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{plan.name}</h3>
                <div className="mb-8">
                  <span className="text-4xl font-bold">${plan.price.toLocaleString()}</span>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Customize Your Package</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(addons).map(([key, addon]) => (
              <div 
                key={key}
                className={`p-6 rounded-lg border-2 transition-all cursor-pointer ${
                  selectedAddons[key] 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => toggleAddon(key)}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-lg">{addon.name}</h4>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    selectedAddons[key] ? 'bg-blue-500' : 'bg-gray-200'
                  }`}>
                    <Check className="h-4 w-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-2">{addon.description}</p>
                <p className="font-medium text-blue-600">${addon.price.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Summary Panel */}
      {shouldShowSummary && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-8">
                <div>
                  <p className="text-sm text-gray-600">Selected Plan</p>
                  <p className="font-semibold">{plans[selectedPlan].name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Add-ons</p>
                  <p className="font-semibold">{getSelectedAddons().length} selected</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Price</p>
                  <p className="text-xl font-bold text-blue-600">${calculateTotal().toLocaleString()}</p>
                </div>
              </div>
              <button
                onClick={handleProceedToCheckout}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-semibold"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h3 className="text-white text-lg font-semibold mb-4">Byte Bridge Services</h3>
            <p className="text-sm">Enterprise-grade machine learning platform for predictive analytics</p>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-sm text-center">
            © 2024 Byte Bridge Services. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;